import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { SaveEventArgs } from '@syncfusion/ej2-angular-grids';
import { SfRequestTypes } from '../../models/syncfusion-data';
import { GridBaseComponent } from './grid-base.component';
import { DynamicEntityDefinition, DynamicEntityFormType, DynamicDerivedFormTypeMarker, DynamicFilterConfig, DynamicFilterOption, resolveEnumTranslationPrefix } from '../dynamic/dynamic-entity-definition';
import { ClientService } from '../../services/client/client.service';
import { TranslateService } from '@ngx-translate/core';
import { ShowInactiveService } from './show-inactive.service';
import { createCrudFormGroup } from '../dynamic/dynamic-form.factory';
import { IDynamicAutocompleteItem } from '../dynamic-autocomplete/dynamic-autocomplete-item';
import { EnumFilterDataManager } from '../syncfusion/enum-filter-data-manager';
import { EnumFilterService } from '../../services/enum-filter.service';
import { DynamicGridFilterFactory } from '../dynamic/dynamic-grid-filter.factory';

type DynamicUpdateModel<TDefinition extends DynamicEntityDefinition<any, any, any>> =
  TDefinition extends DynamicEntityDefinition<any, any, infer TUpdate> ? TUpdate : never;

/**
 * Resolves the concrete form-control map type for a `DynamicGridBaseComponent`.
 *
 * When `T` is `DynamicDerivedFormTypeMarker` (the default), the form type is derived from
 * the entity definition's update model via `DynamicEntityFormType<TDefinition>`. Consumers
 * can still pass an explicit form type to opt out of the derived type (e.g. while migrating
 * a hand-written form type).
 */
type ResolveDynamicFormType<T, TDefinition extends DynamicEntityDefinition<any, any, any>> =
  T extends DynamicDerivedFormTypeMarker ? DynamicEntityFormType<TDefinition> : T;

export abstract class DynamicGridBaseComponent<
  TDefinition extends DynamicEntityDefinition<any, any, any> = DynamicEntityDefinition<any, any, any>,
  T extends { [K in keyof T]: AbstractControl<any>; } = DynamicDerivedFormTypeMarker
> extends GridBaseComponent<ResolveDynamicFormType<T, TDefinition>> {

  public readonly gridColumns = this.crudDefinition.grid.columns;
  public referenceInitialItems: { [fieldKey: string]: IDynamicAutocompleteItem } = {};

  protected constructor(
    public readonly crudDefinition: TDefinition,
    protected fb: FormBuilder,
    clientService: ClientService | null,
    translateService: TranslateService,
    showInactiveService: ShowInactiveService,
    protected enumFilterService: EnumFilterService
  ) {
    super(clientService, translateService, showInactiveService);
  }

  public get isEditMode(): boolean {
    return !!this.gridEditForm?.get(this.crudDefinition.primaryKey)?.value;
  }

  public isPrimaryKeyColumn(field: string): boolean {
    return field === this.crudDefinition.primaryKey;
  }

  public isColumnVisible(field: string, visible?: boolean): boolean {
    if (field === this.isActiveColumnName) {
      return this.showInactive;
    }
    return visible !== false;
  }

  public isFilteringAllowed(allowFiltering?: boolean): boolean {
    return allowFiltering !== false;
  }

  public formatFilter(data: any, field: string, filterConfig?: DynamicFilterConfig): string {
    if (filterConfig?.kind === 'enum' && filterConfig.enumOptions) {
      const prefix = resolveEnumTranslationPrefix(filterConfig.enumOptions);
      return prefix + filterConfig.enumOptions.enumType[data[field]];
    }
    return data.text ?? data[field]?.toString() ?? '';
  }

  public formatColumnValue(field: string, row: any): string {
    const column = this.gridColumns.find(c => c.field === field);
    if (column?.formatValue) {
      return column.formatValue(row, this.translateService);
    }
    return row[field] ?? '';
  }

  public formatBooleanFilter(data: any, field: string, filterConfig?: DynamicFilterConfig): string {
    if (field === this.isActiveColumnName) {
      return this.formatIsActive(data, field);
    }

    var translationKey = filterConfig?.options?.find((option: any) => option.value === data[field])?.textKey;
    if (!translationKey) {
      translationKey = data[field] === true ? 'i18n.shared.yes' : 'i18n.shared.no';
    }
    return translationKey;
  }

  public getColumnFilter(field: string, allowFiltering?: boolean): any {
    if (allowFiltering === false) {
      // create dummy filter
      return DynamicGridFilterFactory.createColumnFilter();
    }
    if (field === this.isActiveColumnName) {
      return this.isActiveFilter;
    }
    const column = this.gridColumns.find(c => c.field === field);
    return DynamicGridFilterFactory.createColumnFilter(column?.filter);
  }

  public override actionBegin(args: SaveEventArgs) {
    if (args.requestType === SfRequestTypes.FilterBeforeOpen) {
      const fargs = args as any;
      const column = this.gridColumns.find(c => c.field === fargs.columnName);
      if (column?.filter?.kind === 'enum') {
        const enumType = column.filter.enumOptions?.enumType;
        const excludeValues = column.filter.enumOptions?.excludeValues ?? undefined;
        fargs.filterModel.options.dataSource = new EnumFilterDataManager(this.enumFilterService.createEnumFilterArray(enumType, fargs.columnName, excludeValues));
      }
    }
    else if (args.requestType === SfRequestTypes.FilterChoiceRequest) {
      const fargs = args as any;
      const fieldName = fargs?.columnName ?? fargs?.field ?? fargs?.column?.field ?? fargs?.filterModel?.options?.field;
      const filterType = fargs?.column?.filter?.type ?? fargs?.filterModel?.options?.type;
      const currentDataSource = fargs?.filterModel?.options?.dataSource ?? fargs?.dataSource;
      const filterChoice = this.getFilterChoiceDataSource(fieldName, filterType, currentDataSource);
      const customDataSource = filterChoice?.dataSource;

      if (filterChoice?.applyDistinctOptimization) {
        if (fargs?.query != null) {
          fargs.query.distincts = []; // will set custom trigger flag and avoid filtering the list for distinct values of the enum filter field
        }
        if (fargs?.filterModel?.options != null) {
          fargs.filterModel.options.dataManager = null;
        }
      }

      if (customDataSource?.length) {
        if (fargs?.filterModel?.options != null) {
          fargs.filterModel.options.dataSource = customDataSource;
          if (customDataSource[0]?.text != null) {
            fargs.filterModel.options.fields = { text: 'text', value: fieldName };
          }
        }
        if (fargs?.dataSource != null) {
          fargs.dataSource = customDataSource;
        }
      }
      return;
    }

    super.actionBegin(args);
  }

  protected override getDataToSave(): any {
    var rawValues = this.gridEditForm?.getRawValue();
    if (rawValues && rawValues[this.crudDefinition.primaryKey as keyof typeof rawValues]) {
      return this.crudDefinition.mapToUpdate ? this.crudDefinition.mapToUpdate(rawValues) : rawValues;
    }
    return this.crudDefinition.mapToCreate ? this.crudDefinition.mapToCreate(rawValues) : rawValues;
  }

  protected getFilterChoiceDataSource(fieldName: string, filterType?: string, currentDataSource?: any): { dataSource: any[]; applyDistinctOptimization?: boolean } | null {
    if (!fieldName) {
      return null;
    }

    const column = this.crudDefinition.grid.columns.find(c => c.field === fieldName as any);
    const options = column?.filter?.options;
    if (!options?.length) {
      return null;
    }

    return {
      dataSource: options.map(option => ({
        [fieldName]: option.value,
        text: option.textKey ? this.translateService.instant(option.textKey) : (option.text ?? option.value?.toString()),
      })),
      applyDistinctOptimization: column?.filter?.kind === 'enum',
    };
  }

  protected override initializeEditForm(data: any): void {
    const preparedData = this.prepareEditFormData(data);
    this.referenceInitialItems = this.extractReferenceInitialItems(data);
    const formGroup = createCrudFormGroup(this.fb, this.crudDefinition.form.fields, preparedData ?? {});
    this.gridEditForm = formGroup as FormGroup<ResolveDynamicFormType<T, TDefinition>>;
    this.afterInitializeEditForm(data, preparedData);
  }

  private extractReferenceInitialItems(data: any): { [fieldKey: string]: IDynamicAutocompleteItem } {
    const items: { [fieldKey: string]: IDynamicAutocompleteItem } = {};
    if (!data) return items;

    for (const field of this.crudDefinition.form.fields) {
      if (field.control === 'reference-select' && field.referenceConfig?.initialValueMapping) {
        const config = field.referenceConfig;
        const sourceObject = data[config.initialValueMapping!.sourceField];
        if (sourceObject) {
          items[field.key] = {
            id: sourceObject[config.valueField],
            name: sourceObject[config.textField],
          };
        }
      }
    }
    return items;
  }

  protected prepareEditFormData(data: any): Partial<DynamicUpdateModel<TDefinition>> {
    return (data ?? {}) as Partial<DynamicUpdateModel<TDefinition>>;
  }

  protected afterInitializeEditForm(originalData: any, preparedData: Partial<DynamicUpdateModel<TDefinition>>): void {
  }
}
